import * as React from "react";
import ReactDOM from "react-dom";
import { isBrowser } from "react-device-detect";
import { cx } from "utils/join-classnames";
import LoaderContainer from "ui/components/atoms/LoaderContainer";
import { IconNames } from "core/models";
import { Icon, Button } from "ui/components";
import styles from "./Modal.styles.module.scss";
import pwa_styles from "./Modal_Pwa.module.scss";

interface ModalProps {
    theme?: "dark" | "light";
    content: React.ReactNode;
    cancelButtonText?: string;
    saveButtonText?: string;
    icon?: IconNames;
    title: string;
    lineColor?: "amber" | "youtube" | "twitter" | "facebook" | "grey";
    fullSize?: boolean;
    pending?: string;
    takeContentSize?: boolean;
    isForm?: boolean;
    onClose?: () => void;
    onSave?: () => void;
    onHeaderClick?: () => void;
    extraButton?: React.ReactNode;
    saveButtonDisabled?: boolean;
    saveButtonVisible?: boolean;
    children?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
    theme,
    children,
    content,
    cancelButtonText,
    saveButtonText,
    icon,
    title,
    lineColor = "amber",
    fullSize,
    pending,
    takeContentSize,
    extraButton,
    isForm = false,
    onClose,
    onSave,
    onHeaderClick,
    saveButtonDisabled = false,
    saveButtonVisible = true,
}) => {
    const pwaModalClasses = cx(
        pwa_styles.container__modal,
        pwa_styles.pwamodal,
        takeContentSize === true ? styles.content__sized : null,
        fullSize === true ? styles.full__size : null,
    );

    const modalClasses = cx(
        styles.container__modal,
        styles.modal,
        takeContentSize === true ? styles.content__sized : null,
        fullSize === true ? styles.full__size : null,
    );

    const headerClasses = cx(
        styles.heading,
        theme === "light" && styles.heading__light,
        lineColor === "amber" && styles.heading__amber,
        lineColor === "youtube" && styles.heading__youtube,
        lineColor === "twitter" && styles.heading__twitter,
        lineColor === "facebook" && styles.heading__facebook,
        lineColor === "grey" && styles.heading__none,
        onHeaderClick !== undefined && styles.heading__clickable,
    );

    return ReactDOM.createPortal(
        <div
            className={
                isBrowser
                    ? cx(
                          styles.container,
                          theme === "dark" ? styles.is__dark : null,
                      )
                    : cx(
                          pwa_styles.container,
                          theme === "dark" ? styles.is__dark : null,
                      )
            }
        >
            {pending && <LoaderContainer message={pending} />}
            <div
                className={
                    isBrowser
                        ? styles.container__background
                        : pwa_styles.container__background
                }
                role="presentation"
                onClick={onClose}
            />
            <div
                className={
                    isBrowser
                        ? styles.container__inner
                        : pwa_styles.container__inner
                }
            >
                <div className={isBrowser ? modalClasses : pwaModalClasses}>
                    <header
                        className={headerClasses}
                        onClick={onHeaderClick}
                        role="presentation"
                    >
                        <div
                            className={
                                isBrowser
                                    ? styles.heading__left
                                    : pwa_styles.heading__left
                            }
                        >
                            {icon && (
                                <span
                                    className={
                                        isBrowser
                                            ? styles.heading__icon
                                            : pwa_styles.heading__icon
                                    }
                                >
                                    <Icon name={icon} />
                                </span>
                            )}
                            <span
                                className={
                                    isBrowser
                                        ? styles.heading__title
                                        : pwa_styles.heading__title
                                }
                            >
                                {title}
                            </span>
                        </div>
                        <div
                            className={
                                isBrowser
                                    ? styles.heading__right
                                    : pwa_styles.heading__right
                            }
                        >
                            {children}
                        </div>
                    </header>
                    <div
                        className={
                            isBrowser
                                ? styles.modal__content
                                : pwa_styles.modal__content
                        }
                    >
                        {content}
                    </div>
                    <div
                        className={
                            isBrowser
                                ? styles.button__container
                                : pwa_styles.button__container
                        }
                    >
                        <Button
                            type="secondary"
                            theme="light"
                            onClick={onClose}
                        >
                            {cancelButtonText ?? "Cancel"}
                        </Button>
                        {!takeContentSize && (
                            <div
                                className={
                                    isBrowser
                                        ? styles.spacer
                                        : pwa_styles.spacer
                                }
                            />
                        )}
                        {extraButton && (
                            <div
                                className={
                                    isBrowser ? styles.extra : pwa_styles.extra
                                }
                            >
                                {extraButton}
                            </div>
                        )}
                        {saveButtonVisible && (
                            <Button
                                type="primary"
                                onClick={onSave}
                                disabled={saveButtonDisabled}
                                action={isForm ? "submit" : "button"}
                            >
                                {saveButtonText ?? "Save"}
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
};
export default Modal;
